import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { useTabs, useTabLabel } from '../../components/shell/TabsContext';
import RecordPage, { FormSection } from '../../components/shell/RecordPage';
import { StatusBadge } from '../../components/datagrid/DataGrid';
import { useCategoryManagerPane, CategorySelect } from '../../components/categories/CategoryManager';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  TreeStructure, 
  FloppyDisk, 
  Eye, 
  BookOpen,
  Gear,
  Calendar,
  CheckCircle,
  Users,
  Clock,
  Shuffle,
  Warning,
  Certificate,
  Trash,
  FolderOpen
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { openLearnerPreview } from '../../components/PreviewMode';

const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'align': [] }],
    ['blockquote', 'code-block'],
    ['link', 'image'],
    ['clean']
  ],
};

const quillFormats = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'list', 'bullet', 'align',
  'blockquote', 'code-block', 'link', 'image'
];

const NEW_COURSE = {
  title: '',
  course_code: '',
  description: '',
  category: '',
  category_id: '',
  is_published: false,
  approved: false,
  display_popup: false,
  control_student_pace: false,
  enable_certificate: true,
  availability_type: 'open',
  start_date: null,
  end_date: null,
  days_from_registration: null,
  days_from_first_logon: null,
};

export default function CourseBuilder() {
  const { courseId } = useParams();
  const isNew = !courseId;
  const { api } = useAuth();
  const navigate = useNavigate();
  const { closeAndNavigate } = useTabs();
  const [course, setCourse] = useState(isNew ? { ...NEW_COURSE } : null);
  const [loading, setLoading] = useState(!isNew);
  useTabLabel(isNew ? 'New course' : (course?.title ? `Edit: ${course.title}` : null));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const categoryMgr = useCategoryManagerPane({ api });
  const settledAt = useRef(Date.now()); // editors normalise values right after mount/load — ignore those
  const [codeError, setCodeError] = useState('');
  const [checkingCode, setCheckingCode] = useState(false);

  useEffect(() => {
    if (!isNew) fetchCourse();
  }, [courseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCourse = async () => {
    try {
      const res = await api.get(`/courses/${courseId}`);
      setCourse(res.data);
      setDirty(false);
      settledAt.current = Date.now();
    } catch (error) {
      toast.error('Failed to load course');
      navigate('/instructor/courses');
    } finally {
      setLoading(false);
    }
  };

  const generateCourseCode = () => {
    const prefix = (course.title || 'CRS').substring(0, 3).toUpperCase().replace(/[^A-Z]/g, 'X');
    const random = Math.random().toString(36).substring(2, 6).toUpperCase();
    const timestamp = Date.now().toString().slice(-4);
    const newCode = `${prefix}-${random}${timestamp}`.substring(0, 20);
    updateCourse('course_code', newCode);
    checkCodeDuplicate(newCode);
  };

  const checkCodeDuplicate = async (code) => {
    if (!code || code.length < 2) {
      setCodeError('');
      return;
    }
    setCheckingCode(true);
    try {
      const res = await api.get(`/courses/check-code/${encodeURIComponent(code)}${courseId ? `?exclude_id=${courseId}` : ''}`);
      if (res.data.exists) {
        setCodeError('This code already exists');
      } else {
        setCodeError('');
      }
    } catch (error) {
      setCodeError('');
    } finally {
      setCheckingCode(false);
    }
  };

  const handleSave = async () => {
    if (codeError) {
      toast.error('Please fix the course code before saving');
      return;
    }
    if (!course.title?.trim()) {
      toast.error('Course title is required');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const res = await api.post('/courses', {
          ...course,
          description: course.description || '',
          category: course.category || null,
          category_id: course.category_id || null,
          course_code: course.course_code || null,
        });
        toast.success('Course created');
        // swap the "New course" tab for the new record's Edit tab
        closeAndNavigate('course:new', `/instructor/courses/${res.data.id}/edit`);
        return;
      }
      await api.put(`/courses/${courseId}`, {
        title: course.title,
        course_code: course.course_code,
        description: course.description,
        category: course.category,
        category_id: course.category_id || null,
        is_published: course.is_published,
        approved: course.approved,
        display_popup: course.display_popup,
        control_student_pace: course.control_student_pace,
        enable_certificate: course.enable_certificate,
        availability_type: course.availability_type,
        start_date: course.start_date,
        end_date: course.end_date,
        days_from_registration: course.days_from_registration,
        days_from_first_logon: course.days_from_first_logon
      });
      toast.success('Course saved successfully');
      setDirty(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (isNew) return;
    if (!window.confirm(`Delete course "${course.title}"? This removes its structure and enrollments.`)) return;
    try {
      await api.delete(`/courses/${courseId}`);
      toast.success('Course deleted');
      closeAndNavigate(`course:edit:${courseId}`, '/instructor/courses');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete course');
    }
  };

  const updateCourse = (field, value) => {
    setCourse(prev => {
      const same = (a, b) => (a ?? '') === (b ?? '');
      if (same(prev[field], value)) return prev;
      // Quill normalises markup on mount — don't count that as a user edit
      const normalised = typeof value === 'string' && typeof prev[field] === 'string'
        && value.replace(/<[^>]+>/g, '').trim() === prev[field].replace(/<[^>]+>/g, '').trim();
      if (!normalised && Date.now() - settledAt.current > 1000) setDirty(true);
      return { ...prev, [field]: value };
    });
    if (field === 'course_code') {
      checkCodeDuplicate(value);
    }
  };

  if (loading || !course) {
    return (
      <DashboardLayout>
        <RecordPage title={loading ? 'Course' : 'Course not found'} loading={loading} onBack={() => navigate('/instructor/courses')}>
          <p className="text-muted-foreground text-center py-12">Course not found</p>
        </RecordPage>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <RecordPage
        data-testid="course-builder"
        title={isNew ? (course.title || 'New course') : course.title}
        subtitle={isNew ? 'Fill in the details and save to create the course' : `Course settings · ${course.course_code || 'no code'}`}
        badge={!isNew && <StatusBadge value={course.is_published ? 'published' : 'draft'} />}
        onBack={() => navigate('/instructor/courses')}
        onRefresh={isNew ? undefined : fetchCourse}
        dirty={dirty}
        primary={[
          { key: 'save', label: saving ? 'Saving…' : isNew ? 'Create course' : 'Save', icon: FloppyDisk, onClick: handleSave, disabled: saving || !!codeError, testid: 'save-course-btn' },
          { key: 'cancel', label: 'Cancel', variant: 'outline', onClick: () => navigate('/instructor/courses') },
        ]}
        side={categoryMgr.side || {
          open: settingsOpen,
          onOpenChange: setSettingsOpen,
          title: 'Course settings',
          icon: Gear,
          defaultSize: 38,
          storageKey: 'course-settings-pane',
          children: (
            <>
      {/* Course Settings Section - Compact */}
      <FormSection title="Course Settings" icon={Gear} bodyClassName="p-3">
          <div className="pane-grid">
            {/* Published */}
            <div className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-green-600" weight="duotone" />
                <div>
                  <Label className="text-xs font-medium cursor-pointer">Published</Label>
                </div>
              </div>
              <Switch
                checked={course.is_published || false}
                onCheckedChange={(c) => updateCourse('is_published', c)}
                data-testid="publish-switch"
              />
            </div>

            {/* Approved */}
            <div className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-blue-600" weight="duotone" />
                <div>
                  <Label className="text-xs font-medium cursor-pointer">Approved</Label>
                </div>
              </div>
              <Switch
                checked={course.approved || false}
                onCheckedChange={(c) => updateCourse('approved', c)}
                data-testid="approved-switch"
              />
            </div>

            {/* Display Pop-up */}
            <div className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-purple-600" weight="duotone" />
                <div>
                  <Label className="text-xs font-medium cursor-pointer">Pop-up</Label>
                </div>
              </div>
              <Switch
                checked={course.display_popup || false}
                onCheckedChange={(c) => updateCourse('display_popup', c)}
                data-testid="display-popup-switch"
              />
            </div>

            {/* Control Student Pace */}
            <div className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-orange-600" weight="duotone" />
                <div>
                  <Label className="text-xs font-medium cursor-pointer">Pace Control</Label>
                </div>
              </div>
              <Switch
                checked={course.control_student_pace || false}
                onCheckedChange={(c) => updateCourse('control_student_pace', c)}
                data-testid="control-pace-switch"
              />
            </div>

            {/* Enable Certificate */}
            <div className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
              <div className="flex items-center gap-2">
                <Certificate className="w-4 h-4 text-amber-600" weight="duotone" />
                <div>
                  <Label className="text-xs font-medium cursor-pointer">Completion Certificate</Label>
                  <p className="text-[10px] text-muted-foreground">Issue a downloadable certificate to learners on completion</p>
                </div>
              </div>
              <Switch
                checked={course.enable_certificate !== false}
                onCheckedChange={(c) => updateCourse('enable_certificate', c)}
                data-testid="enable-certificate-switch"
              />
            </div>
          </div>
      </FormSection>

      {/* Availability Section - Compact */}
      <FormSection title="Availability" icon={Calendar} bodyClassName="p-3 space-y-3">
          <div className="flex flex-wrap items-end gap-4">
            <div className="field">
              <Label className="text-xs font-medium">Availability Type</Label>
              <Select
                value={course.availability_type || 'open'}
                onValueChange={(v) => updateCourse('availability_type', v)}
              >
                <SelectTrigger className="w-[200px]" data-testid="availability-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">
                    <span className="flex items-center gap-2">
                      <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                      Always Available
                    </span>
                  </SelectItem>
                  <SelectItem value="date_based">
                    <span className="flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5 text-blue-500" />
                      Date Based
                    </span>
                  </SelectItem>
                  <SelectItem value="days_from_registration">
                    <span className="flex items-center gap-2">
                      <Users className="w-3.5 h-3.5 text-purple-500" />
                      Days from Registration
                    </span>
                  </SelectItem>
                  <SelectItem value="days_from_logon">
                    <span className="flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5 text-orange-500" />
                      Days from First Log-on
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {course.availability_type === 'date_based' && (
              <>
                <div className="field">
                  <Label htmlFor="start_date" className="text-xs font-medium">Start Date</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={course.start_date || ''}
                    onChange={(e) => updateCourse('start_date', e.target.value)}
                    className="w-[150px]"
                    data-testid="start-date-input"
                  />
                </div>
                <div className="field">
                  <Label htmlFor="end_date" className="text-xs font-medium">End Date</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={course.end_date || ''}
                    onChange={(e) => updateCourse('end_date', e.target.value)}
                    className="w-[150px]"
                    data-testid="end-date-input"
                  />
                </div>
              </>
            )}

            {course.availability_type === 'days_from_registration' && (
              <div className="field">
                <Label htmlFor="days_from_reg" className="text-xs font-medium">Days after enrollment</Label>
                <Input
                  id="days_from_reg"
                  type="number"
                  min="1"
                  value={course.days_from_registration || ''}
                  onChange={(e) => updateCourse('days_from_registration', parseInt(e.target.value) || null)}
                  placeholder="30"
                  className="w-[120px]"
                  data-testid="days-from-reg-input"
                />
              </div>
            )}

            {course.availability_type === 'days_from_logon' && (
              <div className="field">
                <Label htmlFor="days_from_logon" className="text-xs font-medium">Days after first access</Label>
                <Input
                  id="days_from_logon"
                  type="number"
                  min="1"
                  value={course.days_from_first_logon || ''}
                  onChange={(e) => updateCourse('days_from_first_logon', parseInt(e.target.value) || null)}
                  placeholder="60"
                  className="w-[120px]"
                  data-testid="days-from-logon-input"
                />
              </div>
            )}
          </div>
      </FormSection>
            </>
          ),
        }}
        secondary={[
          { key: 'settings', label: settingsOpen ? 'Hide settings' : 'Settings', icon: Gear, variant: settingsOpen ? 'secondary' : 'outline', onClick: () => { categoryMgr.close(); setSettingsOpen((v) => !v); }, testid: 'course-settings-btn' },
          { key: 'categories', label: 'Categories', icon: FolderOpen, onClick: () => { setSettingsOpen(false); categoryMgr.open(); }, testid: 'manage-categories-btn' },
          { key: 'structure', label: 'Structure', icon: TreeStructure, onClick: () => navigate(`/instructor/courses/${courseId}/structure`), disabled: isNew, testid: 'open-structure-builder' },
          { key: 'preview', label: 'Preview', icon: Eye, onClick: () => openLearnerPreview(`/course/${courseId}`), disabled: isNew, testid: 'preview-btn' },
          { key: 'delete', label: 'Delete', icon: Trash, variant: 'destructive', onClick: handleDelete, disabled: isNew },
        ]}
      >
          {/* Course Details Section */}
          <FormSection title="Course Details" icon={BookOpen} bodyClassName="p-3 space-y-3">
              <div className="form-grid">
                <div className="field sm:col-span-2">
                  <Label htmlFor="title" className="text-xs font-medium">Course Title *</Label>
                  <Input
                    id="title"
                    value={course.title || ''}
                    onChange={(e) => updateCourse('title', e.target.value)}
                    placeholder="Enter course title"
                   
                    data-testid="course-title-input"
                  />
                </div>
                <div className="field">
                  <Label htmlFor="course_code" className="text-xs font-medium flex items-center justify-between">
                    <span>Course Code <span className="text-muted-foreground">({(course.course_code || '').length}/20)</span></span>
                  </Label>
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <Input
                        id="course_code"
                        value={course.course_code || ''}
                        onChange={(e) => updateCourse('course_code', e.target.value.toUpperCase().slice(0, 20))}
                        placeholder="e.g., CS101"
                        maxLength={20}
                        className={`font-mono ${codeError ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                        data-testid="course-code-input"
                      />
                      {codeError && (
                        <div className="absolute -bottom-5 left-0 flex items-center gap-1 text-xs text-destructive">
                          <Warning className="w-3 h-3" />
                          {codeError}
                        </div>
                      )}
                    </div>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      className="h-8 px-2"
                      onClick={generateCourseCode}
                      title="Auto-generate code"
                      data-testid="generate-code-btn"
                    >
                      <Shuffle className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

              <div className="field">
                <Label htmlFor="category" className="text-xs font-medium">Category</Label>
                <CategorySelect
                  value={course.category_id}
                  onChange={(v) => updateCourse('category_id', v)}
                  categories={categoryMgr.categories}
                  onManage={() => { setSettingsOpen(false); categoryMgr.open(); }}
                  testid="course-category-select"
                />
                {!course.category_id && course.category && <p className="field-hint">Legacy category “{course.category}” — pick one from the shared list to file this course.</p>}
              </div>
              </div>

              <div className="field">
                <Label className="text-xs font-medium">Description *</Label>
                <div className="border rounded-md overflow-hidden" data-testid="description-editor">
                  <ReactQuill
                    theme="snow"
                    value={course.description || ''}
                    onChange={(value) => updateCourse('description', value)}
                    modules={quillModules}
                    formats={quillFormats}
                    style={{ minHeight: '150px' }}
                    placeholder="Describe what learners will gain from this course..."
                  />
                </div>
              </div>
          </FormSection>


      </RecordPage>
    </DashboardLayout>
  );
}
